/**
 * ResponseLengthContext - exposes the user's chosen reply length
 * (Standard / Detailed) to the Composer's send mutation.
 *
 * Lives across renders of the dashboard; persisted to localStorage
 * so the user's preference survives reloads. Default 'standard'
 * matches the legacy template's first-button-active state.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { ResponseLength } from '../api/conversation';

const STORAGE_KEY = 'platform-ui:response-length';

interface ResponseLengthContextValue {
  length: ResponseLength;
  setLength: (next: ResponseLength) => void;
}

const ResponseLengthContext = createContext<ResponseLengthContextValue | null>(
  null,
);

function readInitialLength(): ResponseLength {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'long' || stored === 'standard') return stored;
  } catch {
    /* localStorage may be unavailable; fall through to default. */
  }
  return 'standard';
}

export function ResponseLengthProvider({ children }: { children: ReactNode }) {
  const [length, setLength] = useState<ResponseLength>(readInitialLength);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, length);
    } catch {
      /* private mode - choice just won't persist. */
    }
  }, [length]);

  return (
    <ResponseLengthContext.Provider value={{ length, setLength }}>
      {children}
    </ResponseLengthContext.Provider>
  );
}

export function useResponseLength(): ResponseLengthContextValue {
  const ctx = useContext(ResponseLengthContext);
  if (!ctx) {
    throw new Error(
      'useResponseLength must be used inside <ResponseLengthProvider>',
    );
  }
  return ctx;
}
